/**
 * The page's notification strip, following the sidebar's export-toast idiom:
 * an inline dismissible banner with a variant-keyed style map, not a new
 * toast library.
 */

import { Bell, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

import {
	requestDesktopNotificationPermission,
	shouldSoftAskForNotifications,
} from "../../utils/desktopNotification";
import { recordSoftAskDismissed } from "../../utils/notificationPreferences";

export type ToastVariant = "success" | "warning" | "error" | "info";

export interface WorkflowToastMessage {
	text: string;
	variant: ToastVariant;
	/**
	 * Carry the "notify me when this finishes" offer (DOP-274).
	 *
	 * Set by the run-start toast when the browser permission is still open and
	 * the user has not waved the offer away. The toast never prompts on its own:
	 * the browser dialog is opened by the click on this line.
	 */
	softAsk?: boolean;
}

interface WorkflowToastProps {
	toast: WorkflowToastMessage | null;
	onDismiss: () => void;
}

const TOAST_STYLES: Record<ToastVariant, string> = {
	success: "border-emerald-200 bg-emerald-50 text-emerald-800",
	warning: "border-amber-200 bg-amber-50 text-amber-800",
	error: "border-red-200 bg-red-50 text-red-800",
	info: "border-gray-200 bg-gray-50 text-text-base",
};

/**
 * The soft-ask outcome, once the user has actually clicked the offer.
 *
 * `idle` is "the offer is still on the table"; the rest are the three answers
 * the browser can come back with, each of which needs a different sentence.
 */
type SoftAskState = "idle" | "granted" | "denied" | "dismissed";

const WorkflowToast: React.FC<WorkflowToastProps> = ({ toast, onDismiss }) => {
	const [softAskState, setSoftAskState] = useState<SoftAskState>("idle");

	// One strip serves every page message in turn. Without this reset, the
	// answer to a run-start offer would still be sitting under the NEXT toast,
	// which is about something else entirely.
	useEffect(() => setSoftAskState("idle"), [toast]);

	const handleEnable = useCallback(() => {
		// This click IS the user gesture the browser wants. Round 1 asked on the
		// Run click instead and Chrome's quieter UI swallowed the prompt.
		void requestDesktopNotificationPermission().then((permission) => {
			if (permission === "granted") setSoftAskState("granted");
			else if (permission === "denied") setSoftAskState("denied");
			else setSoftAskState("dismissed");
		});
	}, []);

	const handleDismiss = useCallback(() => {
		// Dismissing an unanswered offer is a soft "no". Counted, never
		// prompted — after two of them the offer stops appearing.
		if (toast?.softAsk && softAskState === "idle") recordSoftAskDismissed();
		onDismiss();
	}, [onDismiss, softAskState, toast]);

	if (!toast) return null;
	// Re-checked at render, not just at the moment the toast was created: the
	// answer can have been given in another tab, or on the Settings page.
	const offerVisible =
		toast.softAsk === true &&
		softAskState === "idle" &&
		shouldSoftAskForNotifications();

	return (
		<div
			role="status"
			className={`rounded-lg border px-3 py-2 text-sm ${TOAST_STYLES[toast.variant]}`}
		>
			<div className="flex items-start gap-2">
				<span className="flex-1">{toast.text}</span>
				<button
					type="button"
					aria-label="Dismiss notification"
					onClick={handleDismiss}
					className="opacity-70 hover:opacity-100"
				>
					<X className="w-4 h-4" />
				</button>
			</div>
			{offerVisible && (
				<button
					type="button"
					onClick={handleEnable}
					className="mt-1.5 flex items-center gap-1.5 text-sm font-medium underline underline-offset-2 opacity-80 hover:opacity-100"
				>
					<Bell className="w-3.5 h-3.5" />
					<span>Notify me when this finishes</span>
				</button>
			)}
			{softAskState === "granted" && (
				<p className="mt-1.5 text-xs opacity-80">
					You&apos;ll get a desktop notification when this run finishes. Manage
					it in Settings → Notifications.
				</p>
			)}
			{softAskState === "denied" && (
				<p className="mt-1.5 text-xs opacity-80">
					Your browser blocked notifications for Doppler. Settings →
					Notifications has the steps to turn them back on.
				</p>
			)}
		</div>
	);
};

export default WorkflowToast;
